import type { TranslateObject } from '@lumapps/translations';
import type { LegacyWidget } from '@lumapps/widget-base/types';
import { WIDGET_CONTRIBUTION_TYPE } from '@lumapps/widget-contribution/constants';
import { WIDGET_HELP_TYPE } from '@lumapps/widget-help/constants';
import { WIDGET_HTML_TYPE } from '@lumapps/widget-html/constants';
import type { LegacyWidgetHtmlProps } from '@lumapps/widget-html/type';
import { WIDGET_TITLE_TYPE } from '@lumapps/widget-title/constants';
import { isEditorEmpty } from '@lumapps/wrex/slate/utils/isEditorEmpty';

import {
    TranslationDialogFormValues,
    WidgetExtractedData,
    WidgetLangFormValues,
    WidgetSourceData,
    WidgetsExtractedData,
} from '../types';
import { getWidgetBodyTranslations } from './getWidgetBodyTranslations';

interface ParseWidgetsToFormValuesParams {
    widgets: LegacyWidget[];
    sourceLang: string;
    targetLangs: string[];
    contentTitle: TranslateObject<string | null | undefined>;
}

const getWidgetHtmlBodyWithPopin = (
    body: WidgetExtractedData['body'],
    sourceLanguage: string,
): WidgetExtractedData['body'] => {
    const htmlContent = body?.translations?.[sourceLanguage];

    if (htmlContent) {
        const popinContent = body.popinTranslations?.[sourceLanguage];
        const hasPopinLink = htmlContent.includes('<a href="popin"');
        let valueToTranslate = htmlContent;

        // Include popin content only if popin link exists in the HTML
        if (popinContent && hasPopinLink) {
            valueToTranslate += `<div data-popin="true">${popinContent}</div>`;
        }

        return { translations: { ...body.translations, [sourceLanguage]: valueToTranslate } };
    }

    return undefined;
};

/**
 * Extract translation data from widgets once, in a language-agnostic way.
 * This avoids re-iterating widgets when only the language pair changes.
 */
export const extractWidgetsData = ({
    widgets,
    contentTitle,
}: {
    widgets: LegacyWidget[];
    contentTitle: TranslateObject<string | null | undefined>;
}): WidgetsExtractedData => {
    const extracted: WidgetsExtractedData = {};

    for (const widget of widgets) {
        let body: WidgetExtractedData['body'];
        if (widget.widgetType === WIDGET_TITLE_TYPE) {
            body = { translations: contentTitle };
        } else {
            const bodyTranslations = getWidgetBodyTranslations(widget.widgetType, widget.properties);
            const isWidgetHtmlOrHelp = widget.widgetType === WIDGET_HTML_TYPE || widget.widgetType === WIDGET_HELP_TYPE;
            body = bodyTranslations
                ? {
                      translations: bodyTranslations.translations,
                      ...(isWidgetHtmlOrHelp && {
                          popinTranslations: (widget.properties as LegacyWidget<LegacyWidgetHtmlProps>['properties'])
                              ?.popin,
                      }),
                  }
                : undefined;
        }

        extracted[widget.uuid] = {
            title: widget.title,
            body,
            footer: widget.properties?.more?.label,
            type: widget.widgetType,
            uuid: widget.uuid,
        };
    }

    return extracted;
};

/**
 * Parse form values from pre-extracted widget data for a set of target languages.
 *
 * The result is language-first:
 * - `source`: language-agnostic widget metadata (valueToTranslate from source lang, type).
 * - `[lang]`: per-target-language slice, keyed by widget uuid, with a plain boolean
 *   shouldAutoTranslate and the target lang's current fieldRawValue.
 */
export const computeTranslationFormValues = ({
    extractedData,
    sourceLang,
    targetLangs,
}: {
    /** The extracted widgets data provided by {@link extractWidgetsData} */
    extractedData: WidgetsExtractedData;
    sourceLang: string;
    targetLangs: string[];
}): TranslationDialogFormValues => {
    const values: TranslationDialogFormValues = { source: {}, targets: {} };
    const resolvedBodies: Record<string, WidgetExtractedData['body']> = {};

    // Build source (language-agnostic) once
    for (const [uuid, data] of Object.entries(extractedData)) {
        const { title: widgetTitle, footer: widgetFooter, type } = data;
        const isWidgetContribution = type === WIDGET_CONTRIBUTION_TYPE;

        const widgetBody =
            data.type === WIDGET_HTML_TYPE || data.type === WIDGET_HELP_TYPE
                ? getWidgetHtmlBodyWithPopin(data.body, sourceLang) ?? data.body
                : data.body;
        resolvedBodies[uuid] = widgetBody;

        const sourceData: WidgetSourceData = {
            type: data.type,
            title: { valueToTranslate: widgetTitle?.[sourceLang] || undefined },
            body: {
                valueToTranslate:
                    !isWidgetContribution && typeof widgetBody?.translations?.[sourceLang] === 'string'
                        ? widgetBody?.translations?.[sourceLang]
                        : undefined,
            },
            footer: { valueToTranslate: widgetFooter?.[sourceLang] || undefined },
        };

        values.source[uuid] = sourceData;
    }

    // Build per-target-language slices
    for (const targetLang of targetLangs) {
        values.targets[targetLang] = {};

        for (const [uuid, data] of Object.entries(extractedData)) {
            const { title: widgetTitle, footer: widgetFooter, type } = data;
            const isWidgetContribution = type === WIDGET_CONTRIBUTION_TYPE;

            const widgetBody = resolvedBodies[uuid] ?? data.body;

            const widgetBodyTargetValue = isWidgetContribution
                ? widgetBody?.translations?.[targetLang]?.children
                : widgetBody?.translations?.[targetLang];
            const isTargetBodyEmpty =
                isWidgetContribution && widgetBodyTargetValue
                    ? isEditorEmpty({ children: widgetBodyTargetValue })
                    : !widgetBodyTargetValue;

            const langWidget: WidgetLangFormValues = {
                title: {
                    fieldRawValue: widgetTitle?.[targetLang] || '',
                    shouldAutoTranslate: !!widgetTitle?.[sourceLang] && !widgetTitle?.[targetLang],
                },
                body: {
                    fieldRawValue: widgetBodyTargetValue || '',
                    shouldAutoTranslate: !!widgetBody?.translations?.[sourceLang] && isTargetBodyEmpty,
                    ...(isWidgetContribution && {
                        otherProps: { rerenderKey: `${uuid}-${sourceLang}-${targetLang}` },
                    }),
                },
                footer: {
                    fieldRawValue: widgetFooter?.[targetLang] || '',
                    shouldAutoTranslate: !!widgetFooter?.[sourceLang] && !widgetFooter?.[targetLang],
                },
            };

            values.targets[targetLang][uuid] = langWidget;
        }
    }

    return values;
};

/**
 * Apply a bulk auto-translate override to all fields and all target languages in a form values object.
 * - `override=true`: only fields that have a `valueToTranslate` in `source` get shouldAutoTranslate=true
 * - `override=false`: all fields become shouldAutoTranslate=false unconditionally
 */
export const applyAutoTranslateOverride = (
    values: TranslationDialogFormValues,
    override: boolean,
): TranslationDialogFormValues => {
    const result: TranslationDialogFormValues = { source: values.source, targets: {} };

    for (const [lang, widgets] of Object.entries(values.targets)) {
        result.targets[lang] = {};
        for (const [uuid, widget] of Object.entries(widgets)) {
            const srcWidget = values.source[uuid];
            result.targets[lang][uuid] = {
                ...widget,
                title: {
                    ...widget.title,
                    shouldAutoTranslate: override ? Boolean(srcWidget?.title.valueToTranslate) : false,
                },
                body: {
                    ...widget.body,
                    shouldAutoTranslate: override ? Boolean(srcWidget?.body.valueToTranslate) : false,
                },
                footer: {
                    ...widget.footer,
                    shouldAutoTranslate: override ? Boolean(srcWidget?.footer.valueToTranslate) : false,
                },
            };
        }
    }

    return result;
};

/**
 * Parse widgets properties to {@link TranslationDialogFormValues} following those rules:
 * - shouldAutoTranslate needs to be true by default on filled source + empty target
 * - Non-string source body value (e.g richText) should be handled and set in ContentWidgetsTranslationItem
 */
export const parseWidgetsToFormValues = ({
    widgets,
    sourceLang,
    targetLangs,
    contentTitle,
}: ParseWidgetsToFormValuesParams): TranslationDialogFormValues => {
    const extractedData = extractWidgetsData({ widgets, contentTitle });
    return computeTranslationFormValues({ extractedData, sourceLang, targetLangs });
};
